import React, { useEffect, useState } from "react";
import { Loader2, RefreshCw, Search, ShieldCheck, ShoppingCart } from "lucide-react";
import { api } from "@/lib/api";
import { SITEDETAIL } from "@/constants/testIds";
import RetryCloudflareSetupPanel, { LinkExistingZone, AttachExternalDomain } from "./panels/DomainHelpers";

export default function DomainTab({ site, reload }) {
  const [q, setQ] = useState(site.name.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 18));
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [provider, setProvider] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(null);
  const [registrant, setRegistrant] = useState(null);
  const [registrantLoading, setRegistrantLoading] = useState(false);

  useEffect(() => {
    api.get("/sites/domain/status").then(r => setStatus(r.data)).catch(() => {});
  }, []);

  async function loadRegistrant() {
    setRegistrantLoading(true);
    try {
      const r = await api.get("/sites/domain/registrant");
      setRegistrant(r.data);
    } catch {
      setRegistrant(null);
    } finally {
      setRegistrantLoading(false);
    }
  }
  useEffect(() => {
    let cancelled = false;
    api.get("/sites/domain/registrant")
      .then(r => { if (!cancelled) setRegistrant(r.data); })
      .catch(() => { if (!cancelled) setRegistrant(null); });
    return () => { cancelled = true; };
  }, []);

  async function search() {
    setBusy(true); setError("");
    try {
      const r = await api.post("/sites/domain/search", { query: q });
      setResults(r.data.results);
      setProvider(r.data.provider);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }
  async function buy(d) {
    setBusy(true); setError("");
    try {
      const r = await api.post(`/sites/${site.id}/domain/purchase`, { domain: d });
      setProvider(r.data.provider);
      await reload();
      setResults(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }
  const [sweeping, setSweeping] = useState(false);
  const [sweepMsg, setSweepMsg] = useState("");
  async function recheckActivation() {
    setSweeping(true); setSweepMsg("");
    try {
      const r = await api.post(`/domains/activation-sweep`, {});
      const mine = (r.data?.results || []).find(x => (x.domain || "").toLowerCase() === (site.domain || "").toLowerCase());
      await reload();
      if (mine?.activated || mine?.cloudflare_status === "active") setSweepMsg("✓ Zone is now ACTIVE — the site is live.");
      else if (mine?.cloudflare_status) setSweepMsg(`Still ${mine.cloudflare_status} — nudged Cloudflare to re-check nameservers. Give it a few more minutes.`);
      else setSweepMsg("Re-check done. If it's still pending, nameservers are still propagating.");
    } catch (e) {
      setSweepMsg(e.response?.data?.detail || "Re-check failed");
    } finally { setSweeping(false); }
  }
  const liveBadge = provider === "namecheap-live" ? (
    <span className="ml-2 text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded-sm">LIVE</span>
  ) : provider === "mocked" ? (
    <span className="ml-2 text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 bg-amber-500/10 text-amber-400 border border-amber-500/30 rounded-sm">MOCK</span>
  ) : null;

  return (
    <div className="space-y-4">
      {status && !status.configured && (
        <div className="border border-blue-500/30 bg-blue-500/5 text-blue-300 rounded-sm p-3 text-xs mono">
          Namecheap is in <b>MOCK</b> mode. To go live, set <code>NAMECHEAP_API_USER</code> + <code>NAMECHEAP_USERNAME</code> in backend .env and ensure IP <code>{status.client_ip}</code> is whitelisted at Namecheap → Tools → API Access. <span className="text-blue-200">API key ✓</span> · <span className="text-blue-200">Client IP ✓</span>
        </div>
      )}
      {status && status.configured && !status.ok && (
        <div className="border border-red-500/30 bg-red-500/5 text-red-300 rounded-sm p-3 text-xs mono">
          Namecheap configured but live probe FAILING: <b>[{status.error_code}]</b> {status.error}
        </div>
      )}
      {status && status.ok && status.live && (
        <div className="border border-emerald-500/30 bg-emerald-500/5 text-emerald-300 rounded-sm p-3 text-xs mono">
          ✓ Namecheap LIVE ({status.env}) — IP {status.client_ip} whitelisted, auth ok.
        </div>
      )}

      <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-5">
        <div className="label-tiny mb-2">Current Domain</div>
        {site.domain ? (
          <>
          <div className="flex items-center gap-4">
            <div className="font-display text-2xl mono">{site.domain}</div>
            <span className={`dot ${site.cloudflare_status === "active" ? "bg-emerald-500" : "bg-amber-500 animate-pulse"}`} />
            <span className={`text-xs uppercase mono ${site.cloudflare_status === "active" ? "text-emerald-400" : "text-amber-400"}`}>{site.cloudflare_status || "—"}</span>
            {site.cloudflare_zone_id && site.cloudflare_status !== "active" && (
              <button
                data-testid="sitedetail-recheck-activation"
                onClick={recheckActivation}
                disabled={sweeping}
                className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white border border-zinc-700 hover:border-zinc-500 px-2.5 py-1 rounded-sm disabled:opacity-50"
                title="Force Cloudflare to re-check nameservers now instead of waiting for the 10-min sweep"
              >
                {sweeping ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />} Re-check activation
              </button>
            )}
            <div className="ml-auto text-xs text-zinc-500 mono">
              expires: {(site.domain_expires_at || "").slice(0, 10)}
            </div>
          </div>
          {site.cloudflare_status !== "active" && (
            <div className="text-[11px] text-zinc-500 mt-2">
              <b className="text-amber-400">Pending</b> = Cloudflare is waiting to confirm this domain's nameservers point to it. Usually clears within ~10–60 min of purchase; it flips to Active on its own. Use <span className="mono">Re-check activation</span> to nudge it now.
            </div>
          )}
          {sweepMsg && <div data-testid="sitedetail-recheck-msg" className="text-xs mt-2 text-zinc-300 border border-zinc-800 rounded-sm p-2 break-words">{sweepMsg}</div>}
          </>
        ) : (
          <div className="text-sm text-zinc-500 italic">No domain yet. Search and purchase, or link an existing Cloudflare zone below.</div>
        )}
      </div>

      {site.domain && !site.cloudflare_zone_id && (
        <RetryCloudflareSetupPanel site={site} reload={reload} />
      )}

      <LinkExistingZone site={site} reload={reload} />

      <AttachExternalDomain site={site} reload={reload} />

      <div data-testid="sitedetail-registrant-preview" className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-5">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="label-tiny flex items-center gap-2">
              WHOIS Registrant
              {registrant?.live ? (
                <span className="text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded-sm">
                  Namecheap Account
                </span>
              ) : (
                <span className="text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 bg-amber-500/10 text-amber-400 border border-amber-500/30 rounded-sm">
                  Env Fallback
                </span>
              )}
            </div>
            <div className="text-[11px] text-zinc-500 mt-1">
              Used as the ICANN-mandated registrant on every domain purchase. WhoisGuard privacy proxy hides it from public WHOIS lookups.
            </div>
          </div>
          <button
            data-testid="sitedetail-registrant-refresh"
            onClick={loadRegistrant}
            disabled={registrantLoading}
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white disabled:opacity-50"
            title="Re-fetch from Namecheap account"
          >
            {registrantLoading ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />} Refresh
          </button>
        </div>
        {registrant ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div>
              <div className="label-tiny">Name</div>
              <div className="mono text-zinc-200 mt-0.5">{registrant.contact.name || "—"}</div>
            </div>
            <div>
              <div className="label-tiny">Organization</div>
              <div className="mono text-zinc-200 mt-0.5">{registrant.contact.organization || "—"}</div>
            </div>
            <div>
              <div className="label-tiny">Location</div>
              <div className="mono text-zinc-200 mt-0.5">
                {[registrant.contact.city, registrant.contact.state, registrant.contact.country].filter(Boolean).join(", ") || "—"}
              </div>
            </div>
            <div>
              <div className="label-tiny">Postal</div>
              <div className="mono text-zinc-200 mt-0.5">{registrant.contact.postal_code || "—"}</div>
            </div>
            <div>
              <div className="label-tiny">Email</div>
              <div className="mono text-zinc-400 mt-0.5">{registrant.contact.email_masked || "—"}</div>
            </div>
            <div>
              <div className="label-tiny">Phone</div>
              <div className="mono text-zinc-400 mt-0.5">{registrant.contact.phone_masked || "—"}</div>
            </div>
            <div className="col-span-2">
              <div className="label-tiny">WHOIS Privacy</div>
              <div className="mt-0.5 inline-flex items-center gap-1.5 text-emerald-400">
                <ShieldCheck size={14} /> <span className="mono">ON · Free Forever (WhoisGuard)</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-xs text-zinc-500 mono">{registrantLoading ? "Loading registrant…" : "Unable to load registrant info."}</div>
        )}
      </div>

      <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-5">
        <div className="label-tiny mb-3 flex items-center">
          Buy New Domain {liveBadge}
        </div>
        <div className="flex gap-2">
          <input
            data-testid={SITEDETAIL.domainSearchInput}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="admin-input flex-1 bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm mono"
            placeholder="frontierfinance"
          />
          <button
            data-testid={SITEDETAIL.domainSearchButton}
            onClick={search}
            disabled={busy}
            className="inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 px-4 py-2 rounded-sm text-sm font-semibold"
          >
            <Search size={14} /> Search
          </button>
        </div>
        {error && (
          <div className="mt-3 border border-red-500/30 bg-red-500/5 text-red-300 px-3 py-2 rounded-sm text-xs mono">
            {error}
          </div>
        )}
        {results && (
          <div className="mt-4 border border-zinc-800 rounded-sm divide-y divide-zinc-800">
            {results.map(r => (
              <div key={r.domain} className="px-4 py-2.5 flex items-center gap-3">
                <span className={`dot ${r.available ? "bg-emerald-500" : "bg-zinc-600"}`} />
                <div className="mono text-sm flex-1">
                  {r.domain}
                  {r.is_premium && <span className="ml-2 text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 bg-amber-500/10 text-amber-400 border border-amber-500/30 rounded-sm">premium</span>}
                </div>
                <div className="mono text-xs text-zinc-400">${(r.price_usd || 0).toFixed(2)}/yr</div>
                {r.available ? (
                  <button
                    data-testid={SITEDETAIL.domainPurchase(r.domain)}
                    onClick={() => buy(r.domain)}
                    disabled={busy || !!site.domain}
                    className="inline-flex items-center gap-1.5 bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-40 px-3 py-1 rounded-sm text-xs font-semibold"
                  >
                    <ShoppingCart size={12} /> Buy
                  </button>
                ) : (
                  <span className="text-[11px] text-zinc-600 uppercase">taken</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
